<script setup>
/* eslint-env browser */
/* global FB */
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import * as Sentry from '@sentry/vue';
import { useAlert } from 'dashboard/composables';
import { loadScript } from 'dashboard/helper/DOMHelpers';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { useMapGetter } from 'dashboard/composables/store';
import ChannelApi from 'dashboard/api/channels';
import { buildFacebookLoginScopes } from 'dashboard/helper/facebookScopes';

import LoadingState from 'components/widgets/LoadingState.vue';
import Button from 'next/button/Button.vue';
import ComboBox from 'next/combobox/ComboBox.vue';
import ViewWrapper from './ViewWrapper.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const accountId = useMapGetter('getCurrentAccountId');
const inboxesList = useMapGetter('inboxes/getInboxes');

const isConnected = computed(() =>
  inboxesList.value.some(inbox => inbox.channel_type === INBOX_TYPES.FB)
);

const hasLoginStarted = ref(false);
const isCreating = ref(false);
const hasError = ref(false);
const user_access_token = ref('');
const selectedPage = ref({ name: null, id: null });
const pageName = ref('');
const pageList = ref([]);
const emptyStateMessage = ref(t('INBOX_MGMT.DETAILS.LOADING_FB'));
const errorStateMessage = ref('');
const errorStateDescription = ref('');

const rules = computed(() => ({
  pageName: { required },
  selectedPage: {
    isEmpty: () => selectedPage.value !== null && !!selectedPage.value.name,
  },
}));

const v$ = useVuelidate(rules, { pageName, selectedPage });

const showLoader = computed(() => !user_access_token.value || isCreating.value);

const comboBoxPageOptions = computed(() =>
  pageList.value
    .filter(item => !item.exists)
    .map(({ id, name }) => ({ value: id, label: name }))
);

const runFBInit = () => {
  FB.init({
    appId: window.chatwootConfig.fbAppId,
    xfbml: true,
    version: window.chatwootConfig.fbApiVersion,
    status: true,
  });
  window.fbSDKLoaded = true;
  FB.AppEvents.logPageView();
};

const loadFBsdk = () =>
  loadScript('https://connect.facebook.net/en_US/sdk.js', {
    id: 'facebook-jssdk',
  });

const fetchPages = async token => {
  try {
    const response = await ChannelApi.fetchFacebookPages(
      token,
      accountId.value
    );
    const {
      data: { data },
    } = response;
    pageList.value = data.page_details;
    user_access_token.value = data.user_access_token;
  } catch {
    // ignore
  }
};

const tryFBlogin = () => {
  FB.login(
    response => {
      hasError.value = false;
      if (response.status === 'connected') {
        fetchPages(response.authResponse.accessToken);
      } else if (response.status === 'not_authorized') {
        hasError.value = true;
        errorStateMessage.value = t(
          'WGPT_ONBOARDING.CHANNELS.FACEBOOK.ERROR_UNAUTHORIZED'
        );
        errorStateDescription.value = t(
          'WGPT_ONBOARDING.CHANNELS.FACEBOOK.ERROR_UNAUTHORIZED_HELP'
        );
      } else {
        hasError.value = true;
        errorStateMessage.value = t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH');
        errorStateDescription.value = '';
      }
    },
    { scope: buildFacebookLoginScopes() }
  );
};

const startLogin = async () => {
  hasLoginStarted.value = true;
  try {
    await loadFBsdk();
    runFBInit();
    tryFBlogin();
  } catch (error) {
    if (error.name === 'ScriptLoaderError') {
      useAlert(t('WGPT_ONBOARDING.CHANNELS.FACEBOOK.ERROR_LOADING'));
    } else {
      Sentry.captureException(error);
      useAlert(t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
    }
  }
};

const setPageName = pageId => {
  const page = pageList.value.find(p => p.id === pageId);
  if (page) {
    selectedPage.value = page;
    pageName.value = page.name;
  } else {
    selectedPage.value = { name: null, id: null };
    pageName.value = '';
  }
  v$.value.selectedPage.$touch();
};

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$error) return;
  emptyStateMessage.value = t('INBOX_MGMT.DETAILS.CREATING_CHANNEL');
  isCreating.value = true;
  try {
    await store.dispatch('inboxes/createFBChannel', {
      user_access_token: user_access_token.value,
      page_access_token: selectedPage.value.access_token,
      page_id: selectedPage.value.id,
      inbox_name: pageName.value?.trim(),
    });
  } catch {
    // ignore
  } finally {
    isCreating.value = false;
  }
};

onMounted(() => {
  window.fbAsyncInit = runFBInit;
});
</script>

<template>
  <ViewWrapper>
    <div class="flex flex-col gap-6">
      <div v-if="!hasLoginStarted" class="flex flex-col items-start py-8 gap-4">
        <Button
          lg
          icon="i-ri-facebook-circle-line"
          :label="$t('WGPT_ONBOARDING.CHANNELS.FACEBOOK.CONTINUE')"
          @click="startLogin"
        />
        <p class="text-xs">
          {{ $t('INBOX_MGMT.ADD.FB.HELP') }}
        </p>
      </div>

      <div v-else>
        <div v-if="hasError" class="py-8">
          <h5 class="text-n-slate-12 font-medium">{{ errorStateMessage }}</h5>
          <p
            v-if="errorStateDescription"
            v-dompurify-html="errorStateDescription"
            class="mt-2 text-sm text-n-slate-11"
          />
        </div>

        <LoadingState
          v-else-if="showLoader"
          :message="emptyStateMessage"
          class="px-0 !justify-start"
        />

        <form
          v-else
          class="flex flex-col gap-4"
          @submit.prevent="createChannel"
        >
          <div class="input-wrap" :class="{ error: v$.selectedPage.$error }">
            <span class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('INBOX_MGMT.ADD.FB.CHOOSE_PAGE') }}
            </span>
            <ComboBox
              :model-value="selectedPage.id"
              :options="comboBoxPageOptions"
              :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_A_VALUE')"
              :has-error="v$.selectedPage.$error"
              @update:model-value="setPageName"
            />
            <span v-if="v$.selectedPage.$error" class="message mt-0.5">
              {{ $t('INBOX_MGMT.ADD.FB.CHOOSE_PLACEHOLDER') }}
            </span>
          </div>

          <label :class="{ error: v$.pageName.$error }">
            <span class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('INBOX_MGMT.ADD.FB.INBOX_NAME') }}
            </span>
            <input
              v-model="pageName"
              type="text"
              :placeholder="$t('WGPT_ONBOARDING.CHANNELS.FACEBOOK.PICK_NAME')"
              @input="v$.pageName.$touch"
            />
            <span v-if="v$.pageName.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.FB.ADD_NAME') }}
            </span>
          </label>
        </form>
      </div>
    </div>

    <template #actions>
      <Button
        :label="t('WGPT_ONBOARDING.CHANNELS.BACK')"
        variant="link"
        color="slate"
        size="sm"
        @click="router.push({ name: 'wgpt_onboarding_channels' })"
      />
      <Button
        :label="t('WGPT_ONBOARDING.CHANNELS.NEXT')"
        lg
        :is-loading="isCreating"
        :disabled="!isConnected"
        @click="router.push({ name: 'wgpt_onboarding_channels' })"
      />
    </template>
  </ViewWrapper>
</template>
